import { observer } from 'mobx-react-lite';
import {
    AttributeOption,
    CautionsTabLocationsView,
    DispositionsForm,
    EventPanelFormType,
    ConsolidatedLocationView,
} from '@mark43/cad-api';
import { FrPage } from '../../fr-page-layout/fr-page';
import { FrEventPageHeader } from '../../fr-event-common-components/fr-event-page-header';
import { FrPageBody } from '../../fr-page-layout/fr-page-body';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { DispositionForm } from '../../dispositions/components/disposition-form';
import { FrDispositionSummarySection } from '../../fr-event-common-components/fr-disposition/components/fr-disposition-summary-section';
import { UseFormReturn } from 'react-hook-form';
import { FrPageFooter } from '../../fr-page-layout/fr-page-footer';
import { ButtonGroup } from '@cad-arc';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { ROOT_PATH } from '../../router/constants';
import { useDispositionQuery } from '../../dispositions/hooks/use-disposition-query';
import { useNavigate } from 'react-router-dom';
import { useSaveMutationErrorMessage } from '../../common/hooks/use-save-mutation-error-message';

type FrClearOrUnassignPageContentProps = {
    isClearEventPage?: boolean;
    isUnassignCurrentUserUnitPage?: boolean;
    eventId?: number;
    unitId?: number;
    isPrimaryUnit: boolean;
    saveMutationErrorMsg: string;
    dispositionOptions?: AttributeOption[];
    agencyId: number;
    agencyTypeGlobalAttrId: number;
    cautions?: CautionsTabLocationsView;
    areCautionsLoading: boolean;
    eventLocation: ConsolidatedLocationView;
    eventForm: UseFormReturn<EventPanelFormType>;
};

export const FrClearOrUnassignPageContent = observer(function FrClearOrUnassignPageContent({
    isClearEventPage,
    isUnassignCurrentUserUnitPage,
    eventId,
    unitId,
    isPrimaryUnit,
    saveMutationErrorMsg,
    dispositionOptions,
    agencyId,
    agencyTypeGlobalAttrId,
    cautions,
    areCautionsLoading,
    eventLocation,
    eventForm,
}: FrClearOrUnassignPageContentProps) {
    const navigate = useNavigate();
    const saveMutationErrorMessage = useSaveMutationErrorMessage(saveMutationErrorMsg);

    const dispositionQuery = useDispositionQuery({
        agencyEventId: eventId,
    });

    const { isFetching, data } = dispositionQuery;

    const initialValues: DispositionsForm = {
        ...data?.form,
        agencyEventId: eventId ?? 0,
        additionalDispositions: data?.form.additionalDispositions ?? [],
        requestingUnitId: unitId,
        isRequestingUnitPrimaryUnit: isPrimaryUnit,
    };

    return (
        <FrPage>
            <FrEventPageHeader
                isClearEventPage={isClearEventPage}
                isUnassignCurrentUserUnitPage={isUnassignCurrentUserUnitPage}
            />
            <FrPageBody
                customStyles={{
                    padding: '24px',
                    overflowY: 'auto',
                }}
            >
                <FormBody
                    saveErrorMessage={saveMutationErrorMessage}
                    formBodyStyle={{ width: '50%', marginBottom: '48px' }}
                >
                    <DispositionForm
                        isEventClearing={isClearEventPage}
                        isLoading={isFetching}
                        agencyId={agencyId}
                        isPrimaryUnit={isPrimaryUnit}
                        dispositionOptions={dispositionOptions!}
                        initialValues={initialValues}
                    />
                </FormBody>
                <FrDispositionSummarySection
                    cautions={cautions}
                    areCautionsLoading={areCautionsLoading}
                    eventLocation={eventLocation}
                    eventForm={eventForm}
                    agencyTypeGlobalAttrId={agencyTypeGlobalAttrId}
                />
            </FrPageBody>
            <FrPageFooter>
                <ButtonGroup>
                    <FormSaveButton />
                    <FormCancelButton onClick={() => navigate(ROOT_PATH)} />
                </ButtonGroup>
            </FrPageFooter>
        </FrPage>
    );
});
